// Resource Card Component
// Date: January 17, 2025
// Purpose: Card view for Resource entities with link preview support

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LinkIcon,
  DocumentIcon,
  TagIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  StarIcon,
  CalendarIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Resource, resourcesService } from '@/services/resources';
import { fetchLinkPreview } from '@/services/linkPreviewService';

export interface ResourceCardProps {
  resource: Resource;
  onView?: (item: Resource) => void;
  onEdit?: (item: Resource) => void;
  onDelete?: (item: Resource) => void;
  onCopy?: (item: Resource) => void;
  onClick?: (id: string) => void;
  canEdit?: (item: Resource) => boolean;
  customActions?: Array<{
    label: string | ((item: Resource) => string);
    icon: React.ComponentType<any> | ((item: Resource) => React.ComponentType<any>);
    onClick: (item: Resource) => void | Promise<void>;
    variant?: 'primary' | 'secondary' | 'danger' | ((item: Resource) => 'primary' | 'secondary' | 'danger');
  }>;
}

interface LinkPreview {
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
  url?: string;
}

export const ResourceCard: React.FC<ResourceCardProps> = ({
  resource,
  onView,
  onEdit,
  onDelete,
  onCopy,
  onClick,
  canEdit,
  customActions = []
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [linkPreview, setLinkPreview] = useState<LinkPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Fetch link preview for external links
  useEffect(() => {
    if (resource.resource_type === 'link' && resource.url) {
      setPreviewLoading(true);
      
      fetchLinkPreview(resource.url)
        .then((preview) => {
          setLinkPreview(preview);
          setPreviewLoading(false);
        })
        .catch((error) => {
          console.warn('Failed to fetch link preview:', error);
          setPreviewLoading(false);
        });
    }
  }, [resource.resource_type, resource.url]);

  const handleClick = () => {
    if (onClick) {
      onClick(resource.id);
    } else if (onView) {
      onView(resource);
    } else {
      navigate(`/resources/${resource.id}`);
    }
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    e.preventDefault();
    action();
  };

  const handleOpenLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (resource.resource_type === 'link' && resource.url) {
      try {
        await resourcesService.logAccess(resource.id, 'click');
        window.open(resource.url, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Error logging access:', error);
        window.open(resource.url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const isActive = resourcesService.isResourceActive(resource);
  const securityLevel = resourcesService.getSecurityLevel(resource);
  const typeLabel = resourcesService.getResourceTypeLabel(resource);

  const getStatusColor = () => {
    if (!isActive) return 'bg-gray-100 text-gray-800 border-gray-200';
    switch (securityLevel) {
      case 'public': return 'bg-green-100 text-green-800 border-green-200';
      case 'internal': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'confidential': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'sensitive': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    return resourcesService.formatFileSize(bytes);
  };

  return (
    <Card
      variant="outline"
      padding="none"
      className={`hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col ${
        !isActive ? 'opacity-60' : ''
      }`}
      onClick={handleClick}
    >
      <CardContent className="p-4 flex-1 flex flex-col">
        {/* Link Preview Image (for external links) */}
        {resource.resource_type === 'link' && linkPreview?.image && (
          <div className="relative w-full h-40 mb-3 rounded-lg overflow-hidden bg-transparent">
            <img
              src={linkPreview.image}
              alt={linkPreview.title || resource.title}
              className="w-full h-full object-cover"
              style={{ 
                imageRendering: 'auto', 
                opacity: 1,
                filter: 'none',
                mixBlendMode: 'normal'
              }}
              onError={(e) => {
                // Hide image on error
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            {previewLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <div className="flex-shrink-0 mt-0.5">
              {resource.resource_type === 'link' ? (
                <LinkIcon className="w-5 h-5 text-[var(--color-accent-text)]" />
              ) : (
                <DocumentIcon className="w-5 h-5 text-[var(--color-accent-text)]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-[var(--color-secondary-text)] truncate mb-1">
                {linkPreview?.title || resource.title}
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`text-xs border ${getStatusColor()}`}>
                  {securityLevel}
                </Badge>
                <span className="text-xs text-[var(--color-secondary-text)] opacity-75">
                  {typeLabel}
                </span>
                {resource.featured_in_widget && (
                  <StarIcon className="w-4 h-4 text-yellow-500" />
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {onView && (
              <button
                onClick={(e) => handleActionClick(e, () => onView(resource))}
                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                title={t('common.view') || 'View'}
                type="button"
              >
                <EyeIcon className="w-4 h-4" />
              </button>
            )}
            {onEdit && (canEdit ? canEdit(resource) : true) && (
              <button
                onClick={(e) => handleActionClick(e, () => onEdit(resource))}
                className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                title={t('common.edit') || 'Edit'}
                type="button"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
            )}
            {onCopy && (
              <button
                onClick={(e) => handleActionClick(e, () => onCopy(resource))}
                className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                title={t('common.copy') || 'Copy'}
                type="button"
              >
                <DocumentDuplicateIcon className="w-4 h-4" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => handleActionClick(e, () => onDelete(resource))}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                title={t('common.delete') || 'Delete'}
                type="button"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
            {customActions.map((action, index) => {
              const IconComponent = typeof action.icon === 'function' && action.icon.length > 0
                ? (action.icon as (item: Resource) => React.ComponentType<any>)(resource)
                : action.icon as React.ComponentType<any>;
              const variant = typeof action.variant === 'function'
                ? action.variant(resource)
                : action.variant || 'primary';
              const colorClass = variant === 'danger'
                ? 'text-red-600 hover:bg-red-50'
                : variant === 'secondary'
                ? 'text-gray-600 hover:bg-gray-50'
                : 'text-blue-600 hover:bg-blue-50';

              return (
                <button
                  key={index}
                  onClick={(e) => handleActionClick(e, () => action.onClick(resource))}
                  className={`p-1.5 ${colorClass} rounded-md transition-colors`}
                  title={typeof action.label === 'function' ? action.label(resource) : action.label}
                  type="button"
                >
                  <IconComponent className="w-4 h-4" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Description */}
        {(linkPreview?.description || resource.description) && (
          <p className="text-sm text-[var(--color-secondary-text)] opacity-75 line-clamp-2 mb-3">
            {linkPreview?.description || resource.description}
          </p>
        )}

        {/* Link Preview Site Name */}
        {resource.resource_type === 'link' && linkPreview?.siteName && (
          <div className="flex items-center gap-1 mb-2 text-xs text-[var(--color-secondary-text)] opacity-60">
            <ArrowTopRightOnSquareIcon className="w-3 h-3" />
            <span>{linkPreview.siteName}</span>
          </div>
        )}

        {/* Tags */}
        {resource.tags && resource.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {resource.tags.slice(0, 3).map((tag) => (
              <Badge
                key={tag.id}
                className="text-xs bg-gray-100 text-gray-600 border-gray-200"
              >
                <TagIcon className="w-3 h-3 mr-1" />
                {tag.name}
              </Badge>
            ))}
            {resource.tags.length > 3 && (
              <Badge className="text-xs bg-gray-100 text-gray-600 border-gray-200">
                +{resource.tags.length - 3}
              </Badge>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="space-y-1 mb-3 flex-1">
          {resource.resource_type === 'document' && resource.file_size && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-secondary-text)] opacity-75">
              <DocumentIcon className="w-4 h-4" />
              <span>{formatFileSize(resource.file_size)}</span>
            </div>
          )}
          {resource.access_count !== undefined && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-secondary-text)] opacity-75">
              <EyeIcon className="w-4 h-4" />
              <span>{resource.access_count || 0} views</span>
            </div>
          )}
          {resource.last_accessed && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-secondary-text)] opacity-75">
              <CalendarIcon className="w-4 h-4" />
              <span>Last: {new Date(resource.last_accessed).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {/* External Link Button */}
        {resource.resource_type === 'link' && resource.url && (
          <button
            onClick={handleOpenLink}
            className="mt-auto w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
            type="button"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            <span>Open Link</span>
          </button>
        )}
      </CardContent>
    </Card>
  );
};

